import React, { useEffect, useState } from 'react';
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter,
    Button, Text, Input, Select, Textarea, FormControl, FormLabel, Alert, AlertIcon,
    SimpleGrid, Box, Flex, VStack, Divider, Badge,
} from '@chakra-ui/react';
import { FORMAS_PAGAMENTO, formatarCentavos, hojeISO } from '../formato';

// Resumo e confirmacao do pagamento. onConfirmar({ data_pagamento, forma_pagamento,
// observacao }) devolve a mensagem de erro, ou null quando deu certo.
const ModalRegistrarPagamento = ({ isOpen, onClose, vendedor, vendas, compensacoes, onConfirmar }) => {
    const [data, setData] = useState(hojeISO());
    const [forma, setForma] = useState('pix');
    const [observacao, setObservacao] = useState('');
    const [erro, setErro] = useState(null);
    const [enviando, setEnviando] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setData(hojeISO());
            setForma('pix');
            setObservacao('');
            setErro(null);
        }
    }, [isOpen]);

    const somaVendas = vendas.reduce((s, v) => s + v.valor_centavos, 0);
    const somaComp = compensacoes.reduce((s, c) => s + c.sugerido_centavos, 0);
    const total = somaVendas + somaComp;
    const naoConcluidas = vendas.filter((v) => !v.concluida).length;
    const foraDaConta = vendas.filter((v) => v.situacao === 'fora').length;

    const confirmar = async () => {
        if (!data) return setErro('Informe a data do pagamento.');
        if (data > hojeISO()) return setErro('A data do pagamento não pode ser futura.');
        if (total <= 0) return setErro('O total precisa ser maior que zero. Tire alguma compensação ou inclua mais vendas.');
        setEnviando(true);
        setErro(null);
        const falha = await onConfirmar({ data_pagamento: data, forma_pagamento: forma, observacao: observacao.trim() || null });
        setEnviando(false);
        if (falha) setErro(falha);
    };

    return (
        <Modal isOpen={isOpen} onClose={enviando ? () => {} : onClose} isCentered size={{ base: 'full', md: 'xl' }} scrollBehavior="inside">
            <ModalOverlay backdropFilter="blur(2px)" />
            <ModalContent borderRadius={{ base: 0, md: 'xl' }}>
                <ModalHeader pb={1}>Registrar pagamento</ModalHeader>
                <ModalCloseButton isDisabled={enviando} />
                <ModalBody>
                    <Text fontSize="sm" color="gray.600" mb={4}>
                        Confira antes de confirmar. O valor de cada venda fica congelado e o vendedor passa a ver como paga.
                    </Text>

                    <Box borderWidth="1px" borderColor="gray.200" borderRadius="lg" overflow="hidden" mb={4}>
                        <Flex bg="gray.50" px={4} py={2} justify="space-between" align="center">
                            <Text fontSize="sm" fontWeight="semibold" color="gray.700">{vendedor?.name}</Text>
                            <Text fontSize="xs" color="gray.500">{vendas.length} venda(s){compensacoes.length ? `, ${compensacoes.length} compensação(ões)` : ''}</Text>
                        </Flex>
                        <VStack align="stretch" spacing={0} maxH="220px" overflowY="auto" divider={<Divider />} px={4}>
                            {vendas.map((v) => (
                                <Flex key={`v-${v.venda_id}`} py={2} justify="space-between" fontSize="sm" gap={3}>
                                    <Text color="gray.700" noOfLines={1}>
                                        #{v.venda_id}{v.cliente ? ` · ${v.cliente}` : ''}
                                    </Text>
                                    <Text fontWeight="semibold" flexShrink={0}>{formatarCentavos(v.valor_centavos)}</Text>
                                </Flex>
                            ))}
                            {compensacoes.map((c) => (
                                <Flex key={`c-${c.item_id}`} py={2} justify="space-between" fontSize="sm" gap={3}>
                                    <Text color="gray.700" noOfLines={1}>
                                        <Badge colorScheme="purple" mr={2} textTransform="none">Compensação</Badge>
                                        #{c.venda_id}
                                    </Text>
                                    <Text fontWeight="semibold" color={c.sugerido_centavos < 0 ? 'red.600' : 'green.600'} flexShrink={0}>
                                        {formatarCentavos(c.sugerido_centavos)}
                                    </Text>
                                </Flex>
                            ))}
                        </VStack>
                        <Flex bg="orange.50" px={4} py={3} justify="space-between" align="center" borderTopWidth="1px" borderTopColor="orange.100">
                            <Text fontSize="sm" fontWeight="bold" color="gray.700">Total a pagar</Text>
                            <Text fontSize="xl" fontWeight="bold" color={total > 0 ? '#F68633' : 'red.500'}>{formatarCentavos(total)}</Text>
                        </Flex>
                    </Box>

                    {(naoConcluidas > 0 || foraDaConta > 0) && (
                        <Alert status="warning" borderRadius="md" fontSize="sm" mb={4} alignItems="flex-start">
                            <AlertIcon mt={0.5} />
                            <Box>
                                {naoConcluidas > 0 && <Text>{naoConcluidas} venda(s) ainda não concluída(s): se forem canceladas depois, viram compensação no próximo pagamento.</Text>}
                                {foraDaConta > 0 && <Text>{foraDaConta} venda(s) de antes de 01/11/2026 (fora da rastreabilidade) entram neste pagamento.</Text>}
                            </Box>
                        </Alert>
                    )}

                    <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3}>
                        <FormControl isRequired>
                            <FormLabel fontSize="sm" mb={1}>Data do pagamento</FormLabel>
                            <Input type="date" size="sm" borderRadius="md" value={data} max={hojeISO()} onChange={(e) => { setData(e.target.value); setErro(null); }} isDisabled={enviando} aria-label="Data do pagamento" />
                        </FormControl>
                        <FormControl isRequired>
                            <FormLabel fontSize="sm" mb={1}>Forma de pagamento</FormLabel>
                            <Select size="sm" borderRadius="md" value={forma} onChange={(e) => setForma(e.target.value)} isDisabled={enviando} aria-label="Forma de pagamento">
                                {FORMAS_PAGAMENTO.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                            </Select>
                        </FormControl>
                        <FormControl gridColumn={{ sm: '1 / -1' }}>
                            <FormLabel fontSize="sm" mb={1}>Observação</FormLabel>
                            <Textarea size="sm" borderRadius="md" rows={2} maxLength={1000} value={observacao} onChange={(e) => setObservacao(e.target.value)} placeholder="Opcional. Ex.: comprovante enviado no WhatsApp" isDisabled={enviando} aria-label="Observação" />
                        </FormControl>
                    </SimpleGrid>

                    {erro && (
                        <Alert status="error" borderRadius="md" mt={4} fontSize="sm">
                            <AlertIcon />
                            {erro}
                        </Alert>
                    )}
                </ModalBody>
                <ModalFooter gap={2} flexWrap="wrap">
                    <Button variant="ghost" onClick={onClose} isDisabled={enviando}>Cancelar</Button>
                    <Button colorScheme="green" onClick={confirmar} isLoading={enviando} loadingText="Registrando" isDisabled={total <= 0}>
                        Confirmar pagamento de {formatarCentavos(total)}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default ModalRegistrarPagamento;
